import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { getCheckoutStatus } from '../lib/api';

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('checking');
  const [caseId, setCaseId] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const checkStatus = async () => {
      try {
        const result = await getCheckoutStatus(sessionId);

        if (result.payment_status === 'paid') {
          setStatus('success');
          const cid = result.metadata?.case_id;
          setCaseId(cid);
          // Auto-redirect to thank you page after 3 seconds
          if (cid) {
            setTimeout(() => navigate(`/thankyou/${cid}`), 3000);
          }
        } else if (result.status === 'expired') {
          setStatus('failed');
        } else {
          if (attempts < 15) {
            setTimeout(() => setAttempts(prev => prev + 1), 3000);
          } else {
            setStatus('failed');
          }
        }
      } catch (err) {
        console.error('Status check error:', err);
        if (attempts < 8) {
          setTimeout(() => setAttempts(prev => prev + 1), 3000);
        } else {
          setStatus('failed');
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts, navigate]);

  const handleViewReport = () => {
    if (caseId) {
      navigate(`/report/${caseId}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#050608] flex items-center justify-center px-6" data-testid="success-page">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-12">
          <img
            src="/assets/logo-legislai.png"
            alt="FORTUNATO LEGISLAI"
            className="h-14 w-auto mx-auto"
          />
        </div>

        {status === 'checking' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="status-checking"
          >
            <div className="w-20 h-20 rounded-full bg-[#C9A24A]/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#C9A24A] animate-spin" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl text-white mb-4">
              Processando Pagamento
            </h1>
            <p className="text-gray-400">
              Aguarde enquanto confirmamos seu pagamento e geramos seu relatorio...
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-gray-500 text-sm">
              <div className="w-2 h-2 rounded-full bg-[#C9A24A] animate-pulse" />
              Verificando... ({attempts}/15)
            </div>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="status-success"
          >
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl text-white mb-4">
              Pagamento Confirmado!
            </h1>
            <p className="text-gray-400 mb-8">
              Seu relatorio esta sendo gerado. Voce tambem recebera uma copia por e-mail.
            </p>
            <button
              data-testid="view-report-btn"
              onClick={handleViewReport}
              className="px-8 py-4 bg-gradient-to-r from-[#C9A24A] to-[#E0B75B] text-[#050608] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 mx-auto hover:from-[#E0B75B] hover:to-[#C9A24A] transition-all"
            >
              Ver Meu Relatorio
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {status === 'failed' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="status-failed"
          >
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl text-white mb-4">
              Algo deu errado
            </h1>
            <p className="text-gray-400 mb-8">
              Nao foi possivel confirmar seu pagamento. Por favor, tente novamente ou entre em contato.
            </p>
            <button
              data-testid="back-home-btn"
              onClick={() => navigate('/')}
              className="px-8 py-3 border border-[#C9A24A]/50 text-[#C9A24A] font-semibold uppercase tracking-wider text-sm hover:bg-[#C9A24A]/10 transition-colors"
            >
              Voltar ao Inicio
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
